<template>
  <div class="profile-container">
    <div class="profile-header">
      <div class="avatar-container">
        <img :src="avatar || defaultAvatar" alt="Profile Picture" class="profile-avatar" />
      </div>
      <div class="profile-info">
        <div class="name-container">
          <h2>
            {{ profile.first_name }} {{ profile.last_name }}
            <img
              v-if="profile.verified"
              src="@/assets/verified.png"
              alt="Verified"
              class="verified-badge"
              title="Verified Skier"
            />
            <router-link
              v-if="profile.ski_schools && profile.ski_schools.length > 0"
              :to="`/ski-school/${profile.ski_schools[0].slug}`"
              class="ski-school-link"
            >
              <img
                :src="profile.ski_schools[0].logo"
                alt="Ski School"
                class="ski-school-badge"
                :title="`${profile.ski_schools[0].name} - ${profile.ski_schools[0].role}`"
              />
            </router-link>
            <span v-if="profile.verified" class="pro-badge"></span>
          </h2>
        </div>
        <span class="username">@{{ profile.username }}</span>
        <p class="bio">{{ profile.bio }}</p>

        <!-- Disciplines -->
        <div v-if="disciplines.length" class="discipline-badges">
          <span v-for="d in disciplines" :key="d" class="discipline-badge">{{ d }}</span>
        </div>
        
        <!-- 1. SKI (Brand + Model) -->
        <p v-if="profile.ski_brand || profile.ski" class="info-line">
          <img
            v-if="profile.ski_brand"
            :src="require(`@/assets/${profile.ski_brand}.png`)"
            :alt="profile.ski_brand"
            class="ski-brand-logo"
            :title="getBrandDisplayName(profile.ski_brand)"
          />
          <span v-if="profile.ski_brand">{{ getBrandDisplayName(profile.ski_brand) }}</span>
          <span v-if="profile.ski_brand && profile.ski"> - </span>
          <span v-if="profile.ski">{{ profile.ski }}</span>
        </p>
        
        <!-- 2. BOAT -->
        <p v-if="profile.boat" class="info-line">
          <img
            v-if="profile.boat"
            :src="require(`@/assets/${profile.boat}.png`)"
            :alt="profile.boat"
            class="boat-brand-logo"
            :title="getBoatDisplayName(profile.boat)"
          />
          <span v-if="profile.boat">{{ getBoatDisplayName(profile.boat) }}</span>
        </p>
        
        <!-- 3. LAKE -->
        <p v-if="profile.lake" class="info-line">
          <i class="fas fa-water"></i> {{ profile.lake }}
        </p>
        
        <!-- 4. PB -->
        <p v-if="profile.PB" class="info-line">
          <i class="fas fa-trophy"></i> {{ profile.PB }}
        </p>
      </div>
    </div>

    <div class="profile-stats">
      <div class="stat">
        <div class="stat-content">
          <span class="stat-number">{{ profile.posts_count }}</span>
          <p class="stat-label">posts</p>
        </div>
      </div>
      <div class="stat">
        <div class="stat-content">
          <span class="stat-number">{{ profile.tips_provided_count || 0 }}</span>
          <p class="stat-label">tips</p>
        </div>
      </div>
      <div class="stat">
        <router-link
          :to="{ 
            path: isAuthenticated && userId === profile.id 
              ? '/my/followers-following' 
              : `/${profile.username}/followers-following`,
            query: { viewType: 'followers' } 
          }"
          class="stat-link"
        >
          <div class="stat-content">
            <span class="stat-number">{{ profile.followers_count }}</span>
            <p class="stat-label">followers</p>
          </div>
        </router-link>
      </div>
      <div class="stat">
        <router-link
          :to="{ 
            path: isAuthenticated && userId === profile.id 
              ? '/my/followers-following' 
              : `/${profile.username}/followers-following`,
            query: { viewType: 'following' } 
          }"
          class="stat-link"
        >
          <div class="stat-content">
            <span class="stat-number">{{ profile.following_count }}</span>
            <p class="stat-label">following</p>
          </div>
        </router-link>
      </div>
    </div>
    
    <div v-if="isAuthenticated && userId === profile.id" class="profile-buttons-container-full-width">
      <router-link :to="{ name: 'EditProfile' }" class="profile-action-button">
        <i class="fas fa-edit"></i>Edit
      </router-link>
      <router-link to="/ski-settings" class="profile-action-button">
        <i class="fas fa-cog"></i>Ski Settings
      </router-link>
      <button @click="confirmLogout" class="profile-action-button logout-button">
        <i class="fas fa-sign-out-alt"></i>Log Out
      </button>
    </div>
    
    <div class="action-buttons-container">
      <div v-if="isAuthenticated && userId !== profile.id" class="follow-button-container">
        <button
          class="follow-button"
          @click="toggleFollow"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
          :class="{ 'unfollow': isFollowing }"
        >
          {{ getButtonText() }}
        </button>
      </div>
      
      <div v-if="profile.verified" class="request-tip-container">
        <router-link 
          v-if="!isMobile"
          :to="`/request-pro-tip?pro=${profile.user_id}`" 
          class="request-tip-button"
        >
          <i class="fas fa-lightbulb"></i>Request Pro Tip
        </router-link>
        <a 
          v-else
          href="#" 
          @click.prevent="openProTipInBrowser" 
          class="request-tip-button"
        >
          <i class="fas fa-lightbulb"></i>Request Pro Tip
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL, WEB_BASE_URL } from '@/config';
import axios from 'axios';
import eventBus from '@/utils/eventBus';

export default {
  name: 'ProfileContainer',
  props: {
    profile: {
      type: Object,
      required: true
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    userId: {
      type: Number,
      default: null
    },
    avatar: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      defaultAvatar: "https://6buoys-bucket.s3.amazonaws.com/avatar.png",
      isMobile: false,
      isFollowing: false,
      isHovered: false
    }
  },
  computed: {
    hasSkiingStats() {
      return this.profile.ski || this.profile.lake || this.profile.PB;
    },
    disciplines() {
      const result = [];
      if (this.profile.does_slalom) result.push('Slalom');
      if (this.profile.does_trick) result.push('Trick');
      if (this.profile.does_jump) result.push('Jump');
      return result;
    }
  },
  mounted() {
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);
    
    // Check if the user is following this profile
    if (this.isAuthenticated && this.userId !== this.profile.id) {
      this.checkFollowStatus();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkIfMobile);
  },
  methods: {
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 600;
    },
    openProTipInBrowser() {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      // Generate a temporary state parameter for security
      const state = Math.random().toString(36).substring(2, 15);
      
      // Store the state in localStorage to verify later
      localStorage.setItem('auth_state', state);
      
      // Use profile.user_id instead of profile.id to match what RequestProTip expects
      const url = `${WEB_BASE_URL}/request-pro-tip?token=${encodeURIComponent(token)}&state=${state}&pro=${this.profile.user_id}`;
      window.open(url, '_blank');
    },
    async checkFollowStatus() {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        const response = await axios.get(`${API_BASE_URL}/api/user-following/${this.userId}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        // Check if the current profile is in the following list
        this.isFollowing = response.data.some(following => following.receiver === this.profile.id);
      } catch (error) {
        console.error('Error checking follow status:', error);
      }
    },
    getButtonText() {
      if (this.isFollowing) {
        return this.isHovered ? 'Unfollow' : 'Following';
      } else {
        return 'Follow';
      }
    },
    async toggleFollow() {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        if (this.isFollowing) {
          await axios.delete(`${API_BASE_URL}/api/follow-delete/${this.userId}/${this.profile.id}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.isFollowing = false;
          // Emit event instead of directly modifying prop
          this.$emit('follower-count-change', -1);
        } else {
          await axios.post(`${API_BASE_URL}/api/follow-create/${this.userId}/${this.profile.id}/`, null, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.isFollowing = true;
          // Emit event instead of directly modifying prop
          this.$emit('follower-count-change', 1);
        }
      } catch (error) {
        console.error('Error toggling follow:', error);
      }
    },
    confirmLogout() {
      if (confirm('Are you sure you want to log out?')) {
        this.logout();
      }
    },
    logout() {
      // Clear the authentication token
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_state');
      
      // Emit logout event to update navbar and other components
      eventBus.emit('userLoggedOut');
      this.$emit('logout');
      
      // Redirect to login or home page
      this.$router.push('/');
    },
    getBrandDisplayName(brand) {
      const brandChoices = {
        'connelly': 'Connelly',
        'd3': 'D3',
        'goode': 'Goode',
        'ho': 'HO',
        'kd': 'KD',
        'radar': 'Radar',
        'reflex': 'Reflex',
        'obrien': 'Obrien',
        'denali': 'Denali',
        'pineapple': 'Pineapple',
        'lapoint': 'Lapoint',
        'other': 'Other',
      };
      return brandChoices[brand] || brand;
    },
    getBoatDisplayName(boat) {
      const boatChoices = {
        'nautique': 'Nautique',
        'mastercraft': 'MasterCraft',
        'malibu': 'Malibu',
        'centurion': 'Centurion',
        'other': 'Other',
      };
      return boatChoices[boat] || boat;
    }
  }
}
</script>

<style scoped>
.profile-container {
  max-width: 560px;
  margin: 0px auto;
  padding: 20px;
  background-color: #192734;
  border-radius: 16px;
  color: #d9d9d9;
  text-align: left;
}

.profile-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
  position: relative;
}

.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.profile-avatar {
  width: 100px;
  height: 100px;
  min-width: 100px;
  min-height: 100px;
  border-radius: 50%;
  object-fit: cover;
}

.profile-info {
  flex: 1;
}

.profile-info h2 {
  margin: 0;
  color: #1da1f2;
}

.profile-info p {
  margin: 5px 0;
}

.bio {
  color: #8899a6;
}

.discipline-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0;
}

.discipline-badge {
  background-color: #1da1f2;
  color: #ffffff;
  font-size: 0.75em;
  font-weight: bold;
  padding: 3px 10px;
  border-radius: 20px;
}

.profile-stats {
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  margin-left: 20px;
  margin-right: 20px;
}

.stat {
  display: flex;
  align-items: center;
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-number {
  color: #d9d9d9;
  font-size: 1.1em;
  font-weight: bold;
}

.stat-label {
  color: #d9d9d9;
  margin: 0;
  font-size: 1em;
}

.stat-link {
  text-decoration: none;
  color: inherit;
}

.edit-profile-button {
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 10px 20px;
  border-radius: 24px;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  text-decoration: none;
  text-align: center;
  max-width: 26px;
}

.mobile-edit-button {
  display: none;
  width: 80px;
}

.edit-profile-button:hover {
  background-color: #1a91da;
  transform: scale(1.05);
}

.info-line {
  font-size: 0.85em;
  color: #8899a6;
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-line i {
  color: #1da1f2;
  font-size: 0.9em;
}

.name-container {
  display: block;
}

.name-container h2 {
  margin: 0;
  color: #d9d9d9;
  display: flex;
  align-items: center;
  gap: 5px;
}

.username {
  color: #1da1f2;
  font-size: 0.9em;
}

.verified-badge {
  width: 24px;
  height: 24px;
}

.ski-school-badge {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  object-fit: cover;
  margin-bottom: -1px;
}

.ski-school-link {
  text-decoration: none;
  color: inherit;
}

.mobile-edit-button {
  display: block;
}

.action-buttons-container {
  max-width: 560px;
  margin: 16px auto 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.follow-button-container {
  display: flex;
  justify-content: center;
}

.follow-button {
  background-color: #2a3f54;
  color: #ffffff;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 80px;
  box-shadow: 0 0 8px rgba(29, 161, 242, 0.2);
}

.follow-button.unfollow {
  background-color: #2a3f54;
  color: #ffffff;
}

.follow-button.unfollow:hover {
  background-color: rgba(255, 71, 87, 0.3);
  color: #ffffff;
  box-shadow: 0 0 8px rgba(255, 71, 87, 0.3);
}

.follow-button:hover {
  background-color: #375a7f;
  box-shadow: 0 0 12px rgba(29, 161, 242, 0.3);
}

.request-tip-container {
  text-align: center;
}

.request-tip-button {
  display: inline-block;
  padding: 8px 16px;
  background-color: #2a3f54;
  color: #ffffff;
  text-align: center;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 8px rgba(29, 161, 242, 0.2);
}

.request-tip-button i {
  margin-right: 8px;
}

.request-tip-button:hover {
  background-color: #375a7f;
  box-shadow: 0 0 12px rgba(29, 161, 242, 0.3);
}

.profile-buttons-container {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  text-align: left;
}

.profile-buttons-container-full-width {
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 100%;
}

.profile-action-button {
  display: inline-block;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}

.profile-action-button i {
  margin-right: 6px;
  font-size: 11px;
}

.profile-action-button:hover {
  background-color: #1a91da;
  transform: scale(1.02);
}

.logout-button {
  background-color: transparent;
  border: 1px solid #ff4757;
  color: #ff4757;
}

.logout-button:hover {
  background-color: rgba(255, 71, 87, 0.1);
  border-color: #ff3742;
  color: #ff3742;
  transform: scale(1.02);
}

/* Remove the old ski-settings specific styles since we're using shared styles now */
.ski-settings-container {
  /* Remove this entire block */
}

.ski-settings-button {
  /* Remove this entire block */
}

/* Keep the mobile edit button styles for backwards compatibility if needed */
.mobile-edit-button {
  display: none;
}

.ski-brand-logo {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  object-fit: cover;
  margin-bottom: -1px;
}

.boat-brand-logo {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  object-fit: cover;
  margin-bottom: -1px;
}

/* Mobile responsive styles */
@media (max-width: 600px) {
  .profile-stats {
    margin-left: 0;
    margin-right: 0;
  }
}
</style> 